/**
 * ChatInput — autosize textarea with Send/Stop button. Dispatches into the
 * chatStream hook on submit; while streaming, the button flips to Stop and
 * input is disabled per design-lock §1.4.
 *
 * Keyboard:
 *   Enter        → send
 *   Shift+Enter  → newline
 *   Esc          → stop streaming if active, otherwise no-op (panel-level Esc handled elsewhere)
 *
 * Autosize approach: measure scrollHeight on every input, clamp to 8 lines.
 * Keeps things simple — no ResizeObserver, no contenteditable.
 */

import { useEffect, useRef } from 'preact/hooks'
import {
  draftInput,
  chatStreaming,
  canSend,
} from '../../state/chatStore.js'
import { sendChatMessage, stopChatStream } from '../../hooks/useChatStream.js'
import { t } from '../../i18n/index.js'

const MAX_HEIGHT_PX = 8 * 24 // ~8 lines at 24px line-height
const MIN_HEIGHT_PX = 44

function autosize(el) {
  if (!el) return
  el.style.height = 'auto'
  const next = Math.min(Math.max(el.scrollHeight, MIN_HEIGHT_PX), MAX_HEIGHT_PX)
  el.style.height = `${next}px`
  el.style.overflowY = el.scrollHeight > MAX_HEIGHT_PX ? 'auto' : 'hidden'
}

/**
 * @param {object} props
 * @param {object} [props.contextHint] — optional page-context to pass with the message
 * @param {string} [props.placeholder='Ask anything…']
 * @param {boolean} [props.autoFocus=false]
 */
export function ChatInput({
  contextHint = null,
  placeholder,
  autoFocus = false,
}) {
  const resolvedPlaceholder = placeholder ?? t('ion.input.placeholder')
  const ref = useRef(null)
  const value = draftInput.value
  const streaming = chatStreaming.value
  const sendable = canSend.value

  // Autosize on mount + whenever the bound value changes.
  useEffect(() => {
    autosize(ref.current)
  }, [value])

  useEffect(() => {
    if (autoFocus && ref.current) ref.current.focus()
  }, [autoFocus])

  function handleSubmit(e) {
    e?.preventDefault?.()
    if (!sendable || streaming) return
    const text = draftInput.value.trim()
    if (!text) return
    draftInput.value = ''
    sendChatMessage(text, { contextHint })
  }

  function handleKeyDown(e) {
    if (e.key === 'Enter' && !e.shiftKey && !e.isComposing) {
      e.preventDefault()
      handleSubmit(e)
    } else if (e.key === 'Escape' && streaming) {
      e.preventDefault()
      stopChatStream()
    }
  }

  function handleStop() {
    stopChatStream()
  }

  return (
    <form class="ion-input" data-tour="ion-input" onSubmit={handleSubmit}>
      <textarea
        ref={ref}
        class="ion-input__field"
        rows={1}
        value={value}
        placeholder={resolvedPlaceholder}
        disabled={streaming}
        aria-label={t('ion.input.label')}
        onInput={(e) => {
          draftInput.value = e.currentTarget.value
        }}
        onKeyDown={handleKeyDown}
      />
      {streaming ? (
        <button
          type="button"
          class="ion-input__btn ion-input__btn--stop"
          onClick={handleStop}
          aria-label={t('ion.input.stop.aria')}
        >
          <span class="ion-input__stop-icon" aria-hidden="true" />
          {t('ion.input.stop')}
        </button>
      ) : (
        <button
          type="submit"
          class="ion-input__btn ion-input__btn--send"
          disabled={!sendable}
          aria-label={t('ion.input.send.aria')}
        >
          {t('ion.input.send')}
        </button>
      )}
    </form>
  )
}
